import { Link } from 'react-router-dom';
import { formatDate, formatGel, formatGelWhole, formatNumber } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { WalletButtons } from '../components/WalletButtons';
import { Notifications } from '../components/Notifications';
import { ActiveWork } from '../components/ActiveWork';
import { ReferralCard } from '../components/ReferralCard';
import { useCard, useDashboard } from '../lib/queries';
import { LoyaltyCard } from '../components/LoyaltyCard';
import { Amount, Card, Skeleton } from '../components/ui';

export function CardPage() {
  const { t, language } = useI18n();
  const { data: card, isPending: cardPending } = useCard();
  const { data: dashboard } = useDashboard();

  const next = dashboard?.loyalty.nextTier;
  const progressPercent = next && dashboard
    ? Math.min(100, Math.max(2, Math.round(
        (dashboard.loyalty.lifetimeSpendTetri / next.thresholdTetri) * 100,
      )))
    : 100;

  return (
    <div className="space-y-4">
      {/* Above the card: somebody whose car is in the shop opened this to
          find out how it is going, not to look at their points. */}
      <ActiveWork />

      {cardPending && <Skeleton className="h-[30rem]" />}
      {card && <LoyaltyCard card={card} />}

      {dashboard && (
        <>
          {next ? (
            <Card>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
                  {t('card.toNextTier')}
                </span>
                <span className="tabular text-sm font-semibold text-ink">
                  {formatGelWhole(next.remainingTetri, language)} → {next.code}
                </span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand/70 to-brand transition-[width] duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </Card>
          ) : (
            <Card>
              <p className="text-center text-sm text-ink-dim">
                {dashboard.loyalty.tier.code} · {t('card.permanent')}
              </p>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Card>
              <div className="text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
                {t('card.lifetime')}
              </div>
              <Amount
                value={formatGel(dashboard.loyalty.lifetimeSpendTetri, language)}
                className="mt-1.5 block text-lg font-bold text-ink"
              />
            </Card>
            <Card>
              <div className="text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
                {t('card.visits')}
              </div>
              <div className="tabular mt-1.5 text-lg font-bold text-ink">
                {formatNumber(dashboard.loyalty.totalOrders, language)}
              </div>
            </Card>
          </div>

          <Notifications />

          <WalletButtons />

          <ReferralCard />

          {dashboard.recentOrders.length > 0 && (
            <div className="space-y-2">
              {dashboard.recentOrders.slice(0, 3).map((order) => (
                <Card key={order.reference}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="tabular text-sm font-semibold text-ink">{order.reference}</div>
                      <div className="mt-0.5 text-[0.7rem] text-ink-faint">
                        {order.completedAt ? formatDate(order.completedAt, language) : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <Amount
                        value={formatGel(order.orderValueTetri, language)}
                        className="block text-sm font-semibold text-ink"
                      />
                      <div className="tabular mt-0.5 text-[0.68rem] text-positive">
                        +{formatGel(order.cashbackEarnedTetri, language)} · +{formatNumber(order.pointsEarned, language)}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              <Link
                to="/history"
                className="flex min-h-11 items-center justify-center text-center text-xs font-medium text-ink-faint transition-colors hover:text-ink-dim"
              >
                {t('history.title')} →
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
